import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  applyAddressOverride,
  OrderConflictError,
  type OrderAddress,
} from '../../lib/orders';
import {
  applyVerhuizing,
  previewVerhuizing,
  VerhuisError,
  type VerhuisPlan,
} from '../../lib/verhuizing';
import { useAddressLookup, isValidPostcode } from '../../hooks/useAddressLookup';

/**
 * Eén dialoog, twee reikwijdtes. "Ander adres" droeg tot 13-08 twee
 * onverenigbare betekenissen: eenmalig ergens anders afleveren (order-scoped)
 * en de klant is verhuisd (klant-scoped). Alleen de eerste bestond, dus bij een
 * verhuizing bleef het account op het oude adres staan en startte de volgende
 * order weer verkeerd. De reikwijdte is nu een expliciete keuze.
 */

interface AddressOverrideDialogProps {
  orderId: string;
  side: 'pickup' | 'delivery';
  initial: OrderAddress;
  /** Nodig voor de verhuis-verb; ontbreekt hij, dan is alleen order-scope mogelijk. */
  accountId?: string | null;
  onClose: () => void;
  onSaved: (nieuweLastModifiedDate: string | null) => void;
}

type Scope = 'order' | 'verhuizing';

const SIDE_LABEL: Record<'pickup' | 'delivery', string> = {
  pickup: 'ophaaladres',
  delivery: 'afleveradres',
};

function formatPostcode(raw: string): string {
  const clean = raw.replace(/\s/g, '').toUpperCase();
  if (clean.length >= 5 && /^\d{4}[A-Z]/.test(clean)) {
    return `${clean.slice(0, 4)} ${clean.slice(4, 6)}`;
  }
  return clean;
}

function parseStreet(raw: string): { streetName: string; houseNumber: string } {
  const match = raw.trim().match(/^(.+?)\s+(\d+\S*)$/);
  if (match && match[1] && match[2]) {
    return { streetName: match[1].trim(), houseNumber: match[2].trim() };
  }
  return { streetName: raw.trim(), houseNumber: '' };
}

const ACTIE_LABEL: Record<string, string> = {
  pinnen: 'ophaaladres vastzetten',
  meeverhuizen: 'verhuist mee',
  'al-gepind': 'stond al vast',
  onvolledig: 'kan niet',
};

export function AddressOverrideDialog({
  orderId,
  side,
  initial,
  accountId,
  onClose,
  onSaved,
}: AddressOverrideDialogProps) {
  const initialEnabled = initial.override?.enabled === true;
  // Voorvullen met het huidige effectieve adres, ook als de override uit staat.
  // Zonder dit moest een medewerker een bestaand adres overtypen om het vast te
  // zetten, en elke overtypactie kan er stilletjes een ander adres van maken.
  const parsedInitial = parseStreet(initial.street ?? '');

  const [scope, setScope] = useState<Scope>('order');
  const [enable, setEnable] = useState<boolean>(initialEnabled);
  const [postalCode, setPostalCode] = useState<string>(initial.postcode ?? '');
  const [houseNumber, setHouseNumber] = useState<string>(parsedInitial.houseNumber);
  const [houseNumberAddition, setHouseNumberAddition] = useState<string>(initial.toevoeging ?? '');
  const [streetName, setStreetName] = useState<string>(parsedInitial.streetName);
  const [city, setCity] = useState<string>(initial.city ?? '');
  const [forceManual, setForceManual] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [plan, setPlan] = useState<VerhuisPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(false);

  const { lookup, result, isLoading, isManualMode, error: lookupError } = useAddressLookup();

  const veldenActief = scope === 'verhuizing' || enable;

  useEffect(() => {
    if (veldenActief && postalCode && houseNumber) {
      lookup(postalCode, houseNumber);
    }
  }, [veldenActief, postalCode, houseNumber, lookup]);

  useEffect(() => {
    if (!result) return;
    setStreetName(result.street);
    setCity(result.city);
  }, [result]);

  // Het plan hoort bij één adres. Wijzigt de invoer, dan is het plan vervallen.
  useEffect(() => {
    setPlan(null);
  }, [postalCode, houseNumber, houseNumberAddition, streetName, city, scope]);

  const label = SIDE_LABEL[side];
  const postcodeValid = isValidPostcode(postalCode);
  const haveKey = postcodeValid && houseNumber.trim().length > 0;
  const showManualFields = haveKey && (isManualMode || forceManual || (!isLoading && !result));
  const adresCompleet =
    postcodeValid &&
    houseNumber.trim().length > 0 &&
    streetName.trim().length > 0 &&
    city.trim().length > 0;

  const adresPayload = () => ({
    street: `${streetName.trim()} ${houseNumber.trim()}`,
    postcode: formatPostcode(postalCode),
    city: city.trim(),
    toevoeging: houseNumberAddition.trim() || null,
  });

  const handlePreview = async () => {
    if (!accountId || !adresCompleet) return;
    setPlanLoading(true);
    setError(null);
    try {
      setPlan(await previewVerhuizing(accountId, adresPayload()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verhuisplan opstellen mislukt');
    } finally {
      setPlanLoading(false);
    }
  };

  const handleVerhuizing = async () => {
    if (!accountId) return;
    setSubmitting(true);
    setError(null);
    try {
      await applyVerhuizing(accountId, adresPayload());
      onSaved(null);
      onClose();
    } catch (err) {
      if (err instanceof VerhuisError) {
        setError(
          err.accountBijgewerkt
            ? `${err.message} (het klantadres is wél gewijzigd)`
            : err.message
        );
      } else {
        setError(err instanceof Error ? err.message : 'Verhuizing mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverride = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const sidePayload = enable
        ? {
            enable: true,
            street: `${streetName.trim()} ${houseNumber.trim()}`,
            postcode: formatPostcode(postalCode),
            city: city.trim(),
            toevoeging: houseNumberAddition.trim() || undefined,
          }
        : { enable: false };
      const res = await applyAddressOverride(orderId, { [side]: sidePayload });
      onSaved(res.nieuweLastModifiedDate);
      onClose();
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError('Order is ondertussen gewijzigd. Ververs de pagina en probeer opnieuw.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Adres-override mislukt');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmitOverride = enable ? adresCompleet : true;
  const canSubmitVerhuizing = !!plan && plan.blokkades.length === 0 && adresCompleet;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Adres wijzigen</h2>
          <p className="text-xs text-slate-500 mt-1">
            Kies de reikwijdte: raakt dit alleen deze order, of is de klant verhuisd?
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                className="mt-0.5"
                checked={scope === 'order'}
                onChange={() => setScope('order')}
                disabled={submitting}
              />
              <span className="text-sm">
                <span className="text-slate-900 font-medium">Alleen deze order</span>
                <span className="block text-xs text-slate-500">
                  Eenmalig ander {label}. Het adres van de klant blijft ongewijzigd.
                </span>
              </span>
            </label>
            <label
              className={clsx(
                'flex items-start gap-2',
                accountId ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                className="mt-0.5"
                checked={scope === 'verhuizing'}
                onChange={() => setScope('verhuizing')}
                disabled={submitting || !accountId}
              />
              <span className="text-sm">
                <span className="text-slate-900 font-medium">Klant is verhuisd</span>
                <span className="block text-xs text-slate-500">
                  Adres van de klant wijzigen. Lopende orders verhuizen mee, behalve het
                  ophaaladres van orders waar het kleed al is opgehaald.
                </span>
              </span>
            </label>
          </div>

          {scope === 'order' && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={enable}
                onChange={(e) => setEnable(e.target.checked)}
                disabled={submitting}
              />
              <span className="text-sm text-slate-700">Ander {label} gebruiken</span>
            </label>
          )}

          {veldenActief ? (
            <div className="space-y-3">
              <div className="grid grid-cols-6 gap-2">
                <input
                  type="text"
                  placeholder="Postcode"
                  value={postalCode}
                  onChange={(e) => setPostalCode(formatPostcode(e.target.value))}
                  autoComplete="postal-code"
                  disabled={submitting}
                  className="col-span-2 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <input
                  type="text"
                  placeholder="Huisnr"
                  value={houseNumber}
                  onChange={(e) => setHouseNumber(e.target.value)}
                  autoComplete="address-line2"
                  disabled={submitting}
                  className="col-span-2 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <input
                  type="text"
                  placeholder="Toev."
                  value={houseNumberAddition}
                  onChange={(e) => setHouseNumberAddition(e.target.value)}
                  disabled={submitting}
                  className="col-span-2 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {haveKey && isLoading && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <div className="w-3 h-3 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
                  Adres zoeken...
                </div>
              )}

              {result && !forceManual && !isManualMode && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-600">
                    {result.street}, {result.city}
                  </span>
                  <button
                    type="button"
                    onClick={() => setForceManual(true)}
                    disabled={submitting}
                    className="text-indigo-600 hover:underline"
                  >
                    Klopt niet?
                  </button>
                </div>
              )}

              {lookupError && !result && (
                <p className="text-xs text-amber-600">{lookupError}, vul handmatig in</p>
              )}

              {showManualFields && (
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Straat"
                    value={streetName}
                    onChange={(e) => setStreetName(e.target.value)}
                    autoComplete="address-line1"
                    disabled={submitting}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text"
                    placeholder="Plaats"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    autoComplete="address-level2"
                    disabled={submitting}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}
            </div>
          ) : initialEnabled ? (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Het adres wordt teruggezet naar het standaardadres van de klant.
            </div>
          ) : null}

          {scope === 'verhuizing' && (
            <div className="space-y-2">
              {!plan && (
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={!adresCompleet || planLoading || submitting}
                  className={clsx(
                    'text-sm font-medium px-3 py-1.5 rounded border',
                    !adresCompleet || planLoading || submitting
                      ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                      : 'border-indigo-600 text-indigo-600 hover:bg-indigo-50'
                  )}
                >
                  {planLoading ? 'Controleren…' : 'Controleer gevolgen'}
                </button>
              )}

              {plan && (
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                  <div className="px-3 py-2 text-xs text-slate-600">
                    {plan.huidigAdres ? (
                      <>
                        Klantadres {plan.huidigAdres.street}, {plan.huidigAdres.postcode}{' '}
                        {plan.huidigAdres.city} wordt {plan.nieuwAdres.street},{' '}
                        {plan.nieuwAdres.postcode} {plan.nieuwAdres.city}
                      </>
                    ) : (
                      <>
                        Klantadres wordt {plan.nieuwAdres.street}, {plan.nieuwAdres.postcode}{' '}
                        {plan.nieuwAdres.city}
                      </>
                    )}
                  </div>
                  {plan.orders.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-500">
                      Geen lopende orders. Alleen het klantadres wijzigt.
                    </div>
                  ) : (
                    plan.orders.map((o) => (
                      <div key={o.woId} className="px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-slate-900">{o.naam}</span>
                          <span
                            className={clsx(
                              'text-[11px] px-1.5 py-0.5 rounded',
                              o.ophaalActie === 'pinnen'
                                ? 'bg-amber-50 text-amber-800'
                                : o.ophaalActie === 'onvolledig'
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-slate-100 text-slate-600'
                            )}
                          >
                            {ACTIE_LABEL[o.ophaalActie] ?? o.ophaalActie}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{o.toelichting}</p>
                      </div>
                    ))
                  )}
                  {plan.blokkades.length > 0 && (
                    <div className="px-3 py-2 text-xs text-red-700 bg-red-50">
                      {plan.blokkades.join('; ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          {scope === 'order' ? (
            <button
              type="button"
              onClick={handleOverride}
              disabled={submitting || !canSubmitOverride}
              className={clsx(
                'text-sm font-medium px-3 py-1.5 rounded',
                submitting || !canSubmitOverride
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-slate-900 text-white hover:bg-slate-800'
              )}
            >
              {submitting ? 'Opslaan…' : 'Opslaan'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleVerhuizing}
              disabled={submitting || !canSubmitVerhuizing}
              className={clsx(
                'text-sm font-medium px-3 py-1.5 rounded',
                submitting || !canSubmitVerhuizing
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              )}
            >
              {submitting ? 'Verhuizen…' : 'Verhuizing doorvoeren'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
